import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { RefreshCw } from 'lucide-react';
import StockManagerLayout from '../components/StockManagerLayout';
import { API_BASE_URL } from '../utils/apiConfig';
import {
  PULSE_TABS,
  filterPulseRows,
  formatInr,
  formatPct,
  priceTone,
  pulseMovers,
  pulseName,
  sortPulseRows,
  timeAgo,
} from '../utils/pulsePrices';

const REFRESH_MS = 5 * 60 * 1000;
const SCOPES = [
  { id: 'ap', label: 'Andhra Pradesh' },
  { id: 'india', label: 'All India' },
];
const TONE_TEXT = { up: 'text-green-700', down: 'text-red-700', flat: 'text-gray-500' };
const TONE_PILL = {
  up: 'bg-green-50 text-green-700',
  down: 'bg-red-50 text-red-700',
  flat: 'bg-gray-100 text-gray-500',
};
const COLUMNS = [
  { key: 'market', label: 'Mandi' },
  { key: 'pulse', label: 'Pulse' },
  { key: 'min', label: 'Min', num: true, wide: true },
  { key: 'max', label: 'Max', num: true, wide: true },
  { key: 'modal', label: 'Modal', num: true },
  { key: 'changePct', label: 'Change', num: true },
  { key: 'date', label: 'Date', wide: true },
];

const PulsePriceChangesPage = () => {
  const token = useSelector((state) => state.posUser?.userInfo?.token);
  const [scope, setScope] = useState('ap');
  const [pulse, setPulse] = useState('all');
  const [district, setDistrict] = useState('all');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState({ key: 'changePct', dir: 'desc' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [, setClock] = useState(0);

  const load = useCallback(
    async (refresh = false) => {
      if (!token) return;
      setLoading(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/pulse-prices`, {
          params: { scope, ...(refresh ? { refresh: 1 } : {}) },
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(response.data);
        setError('');
      } catch (err) {
        setError(err.response?.data?.message || err.message);
      } finally {
        setLoading(false);
      }
    },
    [scope, token]
  );

  useEffect(() => {
    setDistrict('all');
    setData(null);
    load();
  }, [load]);

  useEffect(() => {
    const refreshTimer = setInterval(() => load(), REFRESH_MS);
    const clockTimer = setInterval(() => setClock((n) => n + 1), 30000);
    return () => {
      clearInterval(refreshTimer);
      clearInterval(clockTimer);
    };
  }, [load]);

  const allRows = useMemo(() => data?.rows || [], [data]);
  const rows = useMemo(
    () => sortPulseRows(filterPulseRows(allRows, { pulse, district, query }), sort),
    [allRows, pulse, district, query, sort]
  );
  const districts = useMemo(() => [...new Set(allRows.map((r) => r.district))].sort(), [allRows]);
  const cards = (data?.summary || []).filter((card) => pulse === 'all' || card.id === pulse);
  const movers = pulseMovers(rows);
  const mandiCount = new Set(rows.map((r) => r.market)).size;

  const toggleSort = (key) =>
    setSort((current) => ({ key, dir: current.key === key && current.dir === 'desc' ? 'asc' : 'desc' }));

  return (
    <StockManagerLayout>
      <main className="space-y-4">
        <section className="rounded-xl border bg-white p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Sale Order Management</p>
              <h1 className="text-xl font-bold text-gray-900">Pulse Price Changes</h1>
              <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
                <span className="inline-flex items-center gap-1.5">
                  <span className={`h-2 w-2 rounded-full ${error && data ? 'bg-amber-500' : 'bg-green-500'}`} />
                  {data ? `Updated ${timeAgo(data.updatedAt)}` : 'Loading…'}
                  {data?.stale ? ' (showing last data)' : ''}
                </span>
                <span>
                  {rows.length} prices · {mandiCount} mandis · ₹ per quintal
                </span>
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex rounded-lg border bg-gray-50 p-1" role="tablist" aria-label="Region">
                {SCOPES.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    role="tab"
                    aria-selected={scope === item.id}
                    onClick={() => setScope(item.id)}
                    className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                      scope === item.id ? 'bg-blue-600 text-white' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => load(true)}
                disabled={loading}
                className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                <span className="hidden sm:inline">Refresh</span>
              </button>
            </div>
          </div>

          <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
            {PULSE_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setPulse(tab.id)}
                className={`shrink-0 whitespace-nowrap rounded-full border px-3 py-1.5 text-sm font-medium ${
                  pulse === tab.id
                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </section>

        {error && (
          <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </p>
        )}

        {!data && loading && <p className="p-4 text-sm text-gray-500">Loading mandi prices…</p>}

        {data && (
          <>
            <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
              {cards.map((card) => {
                const tone = priceTone(card.changePct);
                return (
                  <button
                    key={card.id}
                    type="button"
                    onClick={() => setPulse(card.id)}
                    className={`rounded-xl border bg-white p-4 text-left hover:border-blue-400 ${
                      pulse === card.id ? 'border-blue-500 ring-1 ring-blue-500' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="text-sm font-semibold text-gray-800">{card.label}</h3>
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${TONE_PILL[tone]}`}>
                        {formatPct(card.changePct)}
                      </span>
                    </div>
                    <div className="mt-2 text-2xl font-bold tabular-nums text-gray-900">
                      {formatInr(card.avg)}
                      <span className="ml-1 text-xs font-normal text-gray-400">/qtl avg</span>
                    </div>
                    <div className="text-xs text-gray-500">
                      {card.markets
                        ? `${formatInr(card.low)} – ${formatInr(card.high)} · ${card.markets} mandis`
                        : 'No arrivals reported'}
                    </div>
                    <div className="mt-3 flex justify-between border-t pt-2 text-xs">
                      <span className="text-gray-500">MSP {formatInr(card.msp)}</span>
                      <span className={`font-semibold ${TONE_TEXT[priceTone(card.vsMspPct)]}`}>
                        {card.vsMspPct === null ? '—' : `${formatPct(card.vsMspPct)} vs MSP`}
                      </span>
                    </div>
                  </button>
                );
              })}
            </section>

            <section className="flex flex-wrap gap-x-6 gap-y-1 rounded-xl border bg-white px-4 py-3 text-sm text-gray-600">
              <span>
                <b className="text-green-700">{movers.rising}</b> rising
              </span>
              <span>
                <b className="text-red-700">{movers.falling}</b> falling
              </span>
              {movers.gainer && (
                <span>
                  Top gainer <b className="text-green-700">{movers.gainer.market}</b> · {pulseName(movers.gainer)}{' '}
                  {formatPct(movers.gainer.changePct)}
                </span>
              )}
              {movers.loser && (
                <span>
                  Top loser <b className="text-red-700">{movers.loser.market}</b> · {pulseName(movers.loser)}{' '}
                  {formatPct(movers.loser.changePct)}
                </span>
              )}
            </section>

            <section className="space-y-3 rounded-xl border bg-white p-4">
              <div className="flex flex-wrap gap-2">
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search mandi, district or pulse"
                  aria-label="Search mandi, district or pulse"
                  className="min-w-0 flex-1 rounded border p-2 text-sm"
                />
                <select
                  value={district}
                  onChange={(event) => setDistrict(event.target.value)}
                  aria-label="District"
                  className="min-w-0 rounded border p-2 text-sm sm:w-56"
                >
                  <option value="all">All districts</option>
                  {districts.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      {COLUMNS.map((col) => (
                        <th
                          key={col.key}
                          aria-sort={sort.key === col.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                          className={`p-2 ${col.num ? 'text-right' : 'text-left'} ${col.wide ? 'hidden md:table-cell' : ''}`}
                        >
                          <button type="button" onClick={() => toggleSort(col.key)} className="font-semibold text-gray-700">
                            {col.label}
                            {sort.key === col.key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                          </button>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const tone = priceTone(row.changePct);
                      return (
                        <tr key={`${row.state}|${row.market}|${row.commodity}|${row.variety}|${row.date}`} className="border-t">
                          <td className="p-2">
                            <div className="font-semibold text-gray-900">{row.market}</div>
                            <div className="text-xs text-gray-500">
                              {row.district}
                              {scope === 'india' ? `, ${row.state}` : ''}
                            </div>
                          </td>
                          <td className="p-2">
                            <div>{pulseName(row)}</div>
                            <div className="text-xs text-gray-500">{row.variety}</div>
                          </td>
                          <td className="hidden p-2 text-right tabular-nums md:table-cell">{formatInr(row.min)}</td>
                          <td className="hidden p-2 text-right tabular-nums md:table-cell">{formatInr(row.max)}</td>
                          <td className="p-2 text-right font-semibold tabular-nums">{formatInr(row.modal)}</td>
                          <td className="p-2 text-right">
                            {row.change === null ? (
                              <span className="text-xs text-gray-400">new</span>
                            ) : (
                              <span
                                className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums ${TONE_PILL[tone]}`}
                                title={`Previous ${formatInr(row.previousModal)} on ${row.previousDate}`}
                              >
                                {row.change > 0 ? '▲' : row.change < 0 ? '▼' : '•'} {formatInr(Math.abs(row.change))} (
                                {formatPct(row.changePct)})
                              </span>
                            )}
                          </td>
                          <td className="hidden p-2 text-gray-500 md:table-cell">{row.date}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {!rows.length && (
                  <p className="p-4 text-center text-sm text-gray-500">
                    No pulse arrivals reported for this filter. Mandis usually don't report on Sundays and holidays.
                  </p>
                )}
              </div>

              <p className="text-xs text-gray-400">
                Source: {data.source}. Change compares each mandi's modal price with its previous reported day.
                {data.partial ? ' Some pulses could not be loaded this time.' : ''}
              </p>
            </section>
          </>
        )}
      </main>
    </StockManagerLayout>
  );
};

export default PulsePriceChangesPage;
